import React from "react";
import { Text, NimbusLogo, Toggle } from "../";
import "./Header.scss";
import { CloseIcon, MenuIcon } from "@tiendanube/icons";
import { motion } from "framer-motion";
import classNames from "classnames";
import { fadeDownItem } from "../../utils/constants";

type Props = {
  links: InterfaceHeaderLinks[];
  onClickToggle?: () => void;
  toggleState?: boolean;
  onClickLogo?: () => void;
  style?: React.CSSProperties;
}

export interface InterfaceHeaderLinks {
  children: string;
  onClick: () => void;
}

export type Ref = HTMLDivElement;

const Header = React.forwardRef<Ref, Props>(({
  links,
  onClickToggle,
  toggleState = false,
  onClickLogo,
  style,
}, ref) => {
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => { 
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => 
      window.removeEventListener("resize",updateDimensions);
  }, [])

  const updateDimensions = () => {
    const width = window.innerWidth
    setIsMobile(width < 672)
  }

  const [show, setShow] = React.useState(false);

  function onClickMenu() {
    setShow(!show);
  }

  const menuContainer = {
    open: {
      opacity: 1,
      display: "flex",
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.15,
        ease: "easeOut",
        duration: 0.25,
      }
    },
    closed: {
      opacity: 0,
      transitionEnd: {
        display: "none",
      },
      transition: {
        when: "afterChildren",
        staggerChildren: 0.1,
        ease: "easeOut",
        duration: 0.25,
        staggerDirection: -1,
      }
    }
  }

  const fadeUpLink = {
    open: {
      y: 0,
      opacity: 1,
    },
    closed: {
      y: 15,
      opacity: 0,
    }
  }

  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect (() => {
    document.addEventListener("scroll", e => {
      let scroll = document.scrollingElement.scrollTop;
      if (scroll >= 5) {
        setScrolled(true)
      } else {
        setScrolled(false)
      }
    })
  });

  const isAnimationEnded = React.useRef<boolean>(false);

  const isVisible = React.useMemo(() => isAnimationEnded.current ? "none" : "visible", [isAnimationEnded.current]);

  const className = classNames("nimbus-web--header__wrapper", {"nimbus-web--header__wrapper--scrolled": scrolled});

  const menuRef = React.useRef(null);
  const anchorRef = React.useRef(null);

  const renderLinks = (
    links?.map((link, index) => (
      <motion.button type="button" onClick={link.onClick} ref={anchorRef} variants={fadeUpLink} key={index} className="nimbus-web--header__link">
        <Text size="medium">{link.children}</Text>
      </motion.button>
    ))
  );

  return (
    <motion.div id="nimbus-web--header" ref={ref} className={className} initial="hidden" animate={isVisible} variants={fadeDownItem} onAnimationComplete={() => { isAnimationEnded.current = true; }} style={style}>
      <div className="nimbus-web--header">
        <button type="button" onClick={onClickLogo} className="nimbus-web--header__link">
          <NimbusLogo height={36} />
        </button>
        <div className="nimbus-web--header__links-wrapper">
          {!isMobile &&
            renderLinks
          }
          {onClickToggle &&
            <Toggle on={toggleState} onClick={onClickToggle} />
          }
          {isMobile && <button type="button" onClick={onClickMenu} className="nimbus-web--header__icon">{show ? <CloseIcon size="medium" /> : <MenuIcon size="medium" />}</button>}
        </div>
        {links &&
          <motion.div ref={menuRef} initial={false} animate={show ? "open" : "closed"} variants={menuContainer} className="nimbus-web--menu">
            {renderLinks}
          </motion.div>
        }
      </div>
    </motion.div>
  );
});

export default Header;